// Shared CSV export for the user-management tables.
//
// Every page exports the *whole* filtered dataset, not the visible page, so an
// admin gets the full roster rather than the ten rows currently on screen.

/**
 * Values that Excel and Sheets treat as a formula when a cell starts with them.
 * A name such as "=Sum" or a phone number like "+91..." would otherwise be
 * executed on open, which is the CSV injection problem. Prefixing with an
 * apostrophe keeps the text intact and inert.
 */
const FORMULA_PREFIXES = ["=", "+", "-", "@", "\t", "\r"];

function cell(value) {
  if (value === null || value === undefined) return "";

  // Arrays are common here: classes, departments, children.
  const raw = Array.isArray(value) ? value.filter(Boolean).join("; ") : String(value);
  if (!raw) return "";

  const guarded = FORMULA_PREFIXES.some((prefix) => raw.startsWith(prefix)) ? `'${raw}` : raw;

  // Quote whenever the value could break the row apart.
  return /[",\n\r]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

/** `2026-08-15` — safe in a filename on every platform. */
function today() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Build and download a CSV.
 *
 * @param filename {string} without extension, e.g. "students"
 * @param columns  {Array<{header: string, value: (row) => any}>}
 * @param rows     {Array<object>} the full dataset to export
 * @returns {number} rows written
 */
export function exportToCsv(filename, columns, rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const csvText = buildCsvText(columns, safeRows);

  // The BOM makes Excel read the file as UTF-8, so accented names survive.
  const blob = new Blob(["﻿" + csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}_${today()}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return safeRows.length;
}

export function buildCsvText(columns, rows) {
  const lines = [
    columns.map((column) => cell(column.header)).join(","),
    ...rows.map((row) =>
      columns
        .map((column) => {
          try {
            return cell(column.value(row));
          } catch {
            // One malformed row must not abort the whole export.
            return "";
          }
        })
        .join(",")
    ),
  ];
  return lines.join("\r\n");
}

/**
 * Columns shared by every user export.
 *
 * Headers use the field names the bulk upload writes, so an exported file can
 * be edited and uploaded again to update those accounts. `email` is the match
 * key and is not editable; `readonly_*` columns are for reference only and are
 * ignored on upload.
 */
export const commonUserColumns = [
  { header: "email", value: (u) => u.email },
  { header: "name", value: (u) => u.name },
  { header: "readonly_id", value: (u) => u.id },
  { header: "readonly_role", value: (u) => u.role },
  {
    header: "readonly_created_at",
    value: (u) => (u.created_at ? new Date(u.created_at).toISOString().slice(0, 10) : ""),
  },
];
